import React, { memo, useState } from 'react';
import { Play, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const isVerticalImage = (imagePath: string) => {
  return imagePath.includes('kaylee-bell-stats') || 
         imagePath.includes('brian-michael-video-stats') ||
         imagePath.includes('jordan-harvey-spotify-stats');
};
import type { CaseStudyPageData } from '@/data/caseStudiesPageData';

interface CaseStudyPageCardProps {
  study: CaseStudyPageData;
  index: number;
  isLast?: boolean;
}

const CaseStudyPageCardComponent = ({ study, index, isLast = false }: CaseStudyPageCardProps) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <div className={cn(
      "py-10 lg:py-16 border-b border-white/[0.06] last:border-b-0",
      index === 0 && "pt-6 lg:pt-8"
    )}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Artist Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
            <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
              Success Story #{index + 1}
            </span>
          </div>
        </div>

        {/* Headline */}
        <h3 className="headline-lg text-white text-center mx-auto mb-12 max-w-4xl">
          {index === 0 && (
            <>
              How Brian Got His First 500K Streams Starting{' '}
              <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                From Zero
              </span>
            </>
          )}
          {index === 1 && (
            <>
              How Drew Hit{' '}
              <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                40M+ Streams
              </span>
              {' '}On One Song
            </>
          )}
          {index === 2 && (
            <>
              How Kaylee Went Viral And Gained{' '}
              <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                22M Streams
              </span>
            </>
          )}
          {index === 3 && (
            <>
              How Jordan's First Post Led To{' '}
              <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                1M Streams
              </span>
            </>
          )}
        </h3>

        {/* Content Grid */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start ${isEven ? '' : 'lg:flex-row-reverse'}`}>
          {/* Video + Analytics Column */}
          <div className={`flex flex-col gap-4 ${isEven ? '' : 'lg:order-2'}`}>
            {/* Video Thumbnail */}
            {study.vimeoId ? (
              <button
                onClick={() => setIsVideoOpen(true)}
                className="group relative w-full aspect-video rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.08] transition-all duration-300 hover:border-white/[0.15] hover:shadow-[0_0_40px_rgba(0,113,227,0.2)]"
              >
                {/* Vimeo Thumbnail */}
                <img
                  src={`https://vumbnail.com/${study.vimeoId}.jpg`}
                  alt={`${study.artistName} testimonial video`}
                  className="w-full h-full object-cover transition-all duration-400 group-hover:scale-[1.02] group-hover:brightness-110"
                />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-white/30 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]">
                    <Play className="w-7 h-7 lg:w-8 lg:h-8 text-white fill-white ml-1" />
                  </div>
                </div>

                {/* Artist Name Badge */}
                <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm">
                  <span className="text-sm font-medium text-white">{study.artistName}</span>
                </div>
              </button>
            ) : (
              <div className="w-full aspect-video rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.08] flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <span className="text-sm text-white/40">Video Coming Soon</span>
                </div>
              </div>
            )}

            {/* Analytics Screenshot */}
            {study.analyticsImage && (
              <div className={isVerticalImage(study.analyticsImage) ? "flex justify-center" : ""}>
                <img
                  src={study.analyticsImage}
                  alt={`${study.artistName} analytics`}
                  className={cn(
                    "rounded-xl border border-white/[0.08] transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.4)] cursor-pointer transform-gpu",
                    isVerticalImage(study.analyticsImage) 
                      ? "max-w-[50%]"
                      : "w-full"
                  )}
                  loading="lazy"
                />
              </div>
            )}
          </div>

          {/* Text Content */}
          <div className={`space-y-6 ${isEven ? '' : 'lg:order-1'}`}>
            {/* Paragraphs */}
            <div className="space-y-4">
              {study.paragraphs.map((paragraph, pIndex) => (
                <p key={pIndex} className="text-base text-white/70 leading-relaxed">
                  {paragraph}
                </p>
              ))}
              
              {/* Editorial Spotify Callout */}
              {study.spotifyImage && (
                <div className="flex gap-4 items-start p-4 rounded-xl bg-[#1DB954]/5 border border-[#1DB954]/20 mt-4">
                  <img
                    src={study.spotifyImage}
                    alt={`${study.artistName} Spotify Wrapped`}
                    className="w-24 lg:w-32 rounded-lg border border-white/[0.08] flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-xs font-mono text-[#1DB954] uppercase tracking-wider mb-1.5">
                      Streaming Impact
                    </p>
                    <p className="text-sm text-white/70 leading-relaxed">
                      {study.spotifyImpactText}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* The Secret Box */}
            <div className="p-5 rounded-xl bg-[#0071e3]/5 border border-[#0071e3]/15">
              <p className="text-xs font-mono text-[#0071e3] uppercase tracking-wider mb-2">
                The Secret
              </p>
              <p className="text-sm text-white/80 leading-relaxed">
                {study.secretText}
              </p>
            </div>

            {/* Impact Section */}
            {study.impactTitle && study.impactText && (
              <div className="p-5 rounded-xl bg-[#ec4899]/5 border border-[#ec4899]/15">
                <p className="text-xs font-mono text-[#ec4899] uppercase tracking-wider mb-2">
                  {study.impactTitle}
                </p>
                <p className="text-sm text-white/80 leading-relaxed">
                  {study.impactText}
                </p>
              </div>
            )}
          </div>
      </div>

      {/* Arrow to next case study */}
      {!isLast && (
        <div className="flex justify-center pt-6 lg:pt-8">
          <ChevronDown className="w-6 h-6 text-white/20" />
        </div>
      )}
    </div>

      {/* Video Lightbox Modal */}
      {study.vimeoId && (
        <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
          <DialogContent className="max-w-[95vw] lg:max-w-5xl p-0 bg-black border-white/10">
            <div className="aspect-video w-full">
              <iframe
                src={`https://player.vimeo.com/video/${study.vimeoId}?autoplay=1&title=0&byline=0&portrait=0`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export const CaseStudyPageCard = memo(CaseStudyPageCardComponent);
export default CaseStudyPageCard;
